import { Component, effect, input, OnDestroy, OnInit, output, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatCheckbox, MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { connectFilters, addCheckedProperty, cleanUserFilters } from './more-filters.helpers';
import {
  DataObject,
  filterTypes,
  InputPropOption,
  IOption,
  ISearchFilterSettings,
  MoreFiltersData,
  MoreFiltersOptions,
  MUTUALLY_EXCLUSIVE_FILTERS,
} from '@miq/shared-models';
import { FILTER_SEARCH_DRIVER, LookupRefService, UserLocaleService } from '@miq/platform';
import { SPACE_DEFAULT_FILTER_CONFIG, SPACE_STATUS_FILTER_KEY, isSpaceEntity } from './space-default-filters.config';
import { LeaseColumns, TenancyColumns, SpaceColumns } from '@miq/catalog';
import { isPostalCodeFilter } from '../../components/filters-bar/filters.utils';
import { NgStyle, NgClass, UpperCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTooltip } from '@angular/material/tooltip';
import { MatChipSet, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { OCCUPIER_TYPE_COLUMNS, OCCUPIER_TYPE_SECTION_IDS } from './more-filters.config';
const MAX_NUM_FILTERS = 10;
const DEFAULT_AVAILABILITY_IDS = [317, 318, 5, 2, 320, 319, 17152, 3895, 3];
const SUMMARY_LABEL = 'Summary';
const LEASE_EVENT_COLUMNS = [LeaseColumns.LeaseDetails_LeaseEvents, TenancyColumns.Tenancy_LeaseEvents];

/**
 * L2-S11: inlined from `src/app/records/records-search/records-search.constants.ts` (POPUP_TYPE).
 * Only `.recordList` is read here, and line ~139 of this file already compares the same value as a
 * raw `'record-list'` string. That constants file is records-domain — a library must never import it
 * (it drags the records search graph in), so the two keys are copied verbatim instead.
 */
const POPUP_TYPE = {
  recordList: 'record-list',
  tableSettings: 'table-settings',
};

/**
 * L2-S11: inlined from `src/app/records/controls/custom/tim/view-spaces-from-tim/filters.ts`
 * (`defaultIDs`, line 715) — the Spain-user availability filter allow-list. Records-domain module,
 * and importing it from a library would create a runtime import cycle (that file is part of the TIM
 * filters graph this dialog is being separated from), so the 22 ids are copied exactly.
 */
const defaultIDs = [
  317, 318, 319, 320, 305, 5, 6, 2, 1, 8, 3895, 18320, 18604, 19149, 3722, 3723, 18480, 18481, 17378, 17305, 206, 20,
];

/**
 * L2-S11: inlined from the same records-domain file as `defaultIDs`
 * (`view-spaces-from-tim/filters.ts`, `FILTER_EMPTY_VALUE`, line 632) — the placeholder option
 * prepended to dropdown lookups. Copied rather than imported for the same reason: that module is
 * records-domain and pulling it into a library would re-create the import cycle.
 */
const FILTER_EMPTY_VALUE: InputPropOption & { code?: string } = {
  key: null,
  value: null,
  name: '--',
  actualValue: null,
  code: '--',
};

/**
 * L2-S11: inlined from
 * `src/app/records/controls/common/listeners/space/space-terms-listener.model.ts` (line 141) — the
 * space tenancy comment columns hidden from the filter catalog. Records-domain listener model; a
 * library must not import it, so the five SpaceColumns ids are copied exactly.
 */
const SPACE_TENANCY_COMMENTS_COLUMNS = [
  SpaceColumns.Space_Tenancy_Comment,
  SpaceColumns.Space_Tenancy_UserEmail,
  SpaceColumns.Space_Tenancy_UserName,
  SpaceColumns.Space_Tenancy_Comment_DT,
  SpaceColumns.Space_Tenancy_CommentType,
];
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-more-filters',
  templateUrl: './more-filters.component.html',
  styleUrl: './more-filters.component.scss',
  providers: [
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions },
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    CdkScrollable,
    MatDialogContent,
    NgStyle,
    MatCheckbox,
    NgClass,
    MatTooltip,
    MatChipSet,
    MatChipOption,
    MatChipRemove,
    MatDialogActions,
    UpperCasePipe,
    L10nTranslationModule
],
})
export class MoreFiltersComponent implements OnInit, OnDestroy {
  readonly dataFiltersInput = input<MoreFiltersData>(undefined, { alias: 'dataFilters' });
  isModal = input<boolean>();
  filtersChange = output<ISearchFilterSettings[]>()
  readonly defaultData: MoreFiltersData = {
    options: { title: '', subTitle: '', message: '' },
    userFilters: [],
    filtersCatalog: [],
    filterForRecords: false,
    filterForTableView: false,
    defaultFilters: [],
    isFilter: false,
    isCatalogData: false,
  };
  data: MoreFiltersData;
  filtersTitle: string;
  dialogOptions: MoreFiltersOptions;
  filtersCatalog: ISearchFilterSettings[];
  userFilters: ISearchFilterSettings[];
  defaultColumns: ISearchFilterSettings[];
  searchFormControl: FormControl<string | null>;
  isFilterDialog = false;
  filterForRecords = false;
  filterForTableView = false;
  isCatalogData = false;
  private forReset: ISearchFilterSettings[];
  private fullCatalog: ISearchFilterSettings[];
  private readonly isSpainUserFlag = this.userLocale.isSpainUser();
  /**
   * L2-S11: was `RecordsSearchStateService` injected by CLASS. Optional on purpose — the reports
   * remote renders this component with no driver provided, and every access below is guarded.
   */
  private readonly searchDriver = inject(FILTER_SEARCH_DRIVER, { optional: true });
  constructor(
    private readonly dialogRef: MatDialogRef<MoreFiltersComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly lookupRefService: LookupRefService,
    private userLocale: UserLocaleService
  ) {
    effect(() => {
      const data = this.dataFiltersInput();
      if (data == null || data === this.lastProcessedDataFilters) return;
      this.lastProcessedDataFilters = data;
      this.data = data;
      this.setFiltersData({ ...this.defaultData, ...data });
    });
  }
  private lastProcessedDataFilters: unknown = null;
  ngOnInit() {
    /* required for static label translation */
  }
  setFiltersData(data: MoreFiltersData): void {
    const {
      options,
      userFilters,
      filtersCatalog,
      filterForRecords,
      filterForTableView,
      defaultFilters,
      isFilter,
      isCatalogData,
    } = data;
    // Set component properties
    Object.assign(this, {
      isCatalogData,
      dialogOptions: options,
      isFilterDialog: isFilter,
      userFilters: userFilters ?? [],
      defaultColumns: defaultFilters,
      filterForRecords,
      filterForTableView,
    });
    // Initialize search form control
    this.searchFormControl = this.formBuilder.control('');
    this.searchFormControl.valueChanges.subscribe(this.searchFilter.bind(this));
    // Filter out summary section and connect filters
    this.filtersCatalog = connectFilters(filtersCatalog, this.userFilters).filter(
      filterObj => filterObj.title !== SUMMARY_LABEL
    );
    // Handle search matching availability configuration
    if (options.searchMatchingAvailability) {
      this.configureAvailabilityFilters();
    }
    // Hide lease events filter for record list popup
    if (options.popupFrom === 'record-list') {
      this.hideLeaseEventsFilter();
    }
    this.ensureSpaceStatusDefaults();
    // Hide space tenancy events filter && Occupier Type column in Lease
    this.hideSpaceTenancyEventsFilter();
    // Create deep copies for reset functionality
    this.forReset = this.createDeepCopy(this.userFilters);
    this.fullCatalog = this.createDeepCopy(this.filtersCatalog);
  }
  private configureAvailabilityFilters(): void {
    // Use API-provided IDs when available; fall back to hardcoded lists
    const apiIds = this.data?.options?.defaultAvailabilityFilterIds;
    const defaultIds = new Set<number>(
      apiIds?.length > 0 ? apiIds : (this.isSpainUserFlag ? defaultIDs : DEFAULT_AVAILABILITY_IDS)
    );
    this.filtersCatalog.forEach(section => {
      section.filters.forEach((filter: DataObject) => {
        filter.display = true;
        // Postal Code has no lookupCategory - it's a free-text field, not a real options-backed
        // dropdown - so treat it as text rather than letting it hit the Dropdown-without-a-lookup
        // exclusion (or the lookup-based dropdown config in onCheck) below.
        if (isPostalCodeFilter(filter)) {
          filter.inputType = 'text';
        }
        //Global Change(CE-4416) : enable dropdown filters for all by default
        if (
          (filter.inputType === filterTypes.naics && !defaultIds.has(filter.key)) ||
          (filter?.inputType === filterTypes.Dropdown && !filter?.lookup)
        ) {
          filter.display = false;
        }
        // Configure default ID filters
        if (defaultIds.has(filter.key)) {
          filter.checked = true;
          filter.isFilterDisabled = true;
        }
      });
    });
  }
  private createDeepCopy<T>(items: T[]): T[] {
    return items.map(item => (item ? { ...item } : undefined));
  }
  onCkboxToggle(checkbox: MatCheckbox, sectionId: number, title: string, filter: IOption): void {
    if (filter.isFilterDisabled) {
      return;
    }

    if (!this.filterForTableView && !checkbox.checked && MUTUALLY_EXCLUSIVE_FILTERS[filter.key]) {
      this.removeMutuallyExclusiveFilter(MUTUALLY_EXCLUSIVE_FILTERS[filter.key], sectionId);
    }

    const shouldCheck = !checkbox.checked && this.isUserFiltersFull();
    shouldCheck ? this.onCheck(title, filter, sectionId) : this.onUncheck(title, filter);
    if (!this.isModal()) {
      this.filtersChange.emit(this.userFilters);
    }
  }
  onRemove(title: string, removed: IOption): void {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    if (userFilter) {
      userFilter.filters = userFilter.filters.filter(usrFilter => usrFilter !== removed);
      this.userFilters = cleanUserFilters(this.userFilters);
      removed['checked'] = false;
    }
  }
  onClear(): void {
    const hadSpaceStatusFilter = this.userFilters.some(userFilter =>
      userFilter.filters.some(filter => filter.key === SPACE_STATUS_FILTER_KEY)
    );
    this.userFilters = [];
    this.filtersCatalog = this.filtersCatalog.map(catalogFilter => ({
      ...catalogFilter,
      filters: catalogFilter.filters.map(filter => ({ ...filter, checked: false })),
    }));
    const currentEntity = this.searchDriver?.activeQueryState?.entity;
    if (hadSpaceStatusFilter && isSpaceEntity(currentEntity)) {
      this.reAddSpaceStatusWithDefaults();
    }
  }
  /**
   * Space Status filter re-addition
   */
  private reAddSpaceStatusWithDefaults(): void {
    const spaceStatusCatalogFilter = this.findSpaceStatusFilter();
    if (spaceStatusCatalogFilter) {
      const filterWithDefaults = {
        ...spaceStatusCatalogFilter.filter,
        values: [...SPACE_DEFAULT_FILTER_CONFIG[SpaceColumns.SpaceDetails_SpaceStatus].values], // Create new array to prevent mutation
        checked: true,
      };
      this.userFilters.push({
        title: spaceStatusCatalogFilter.section.title,
        filters: [filterWithDefaults],
        sectionId: spaceStatusCatalogFilter.section.sectionId,
      });
      spaceStatusCatalogFilter.filter.checked = true;
    }
  }
  onReset(): void {
    this.userFilters = this.forReset.map(usrFilter => ({ ...usrFilter }));
    this.filtersCatalog = connectFilters(this.data.filtersCatalog || [], this.userFilters);
  }
  addFilters(): void {
    if (this.isModal()) {
      this.filtersChange.emit(this.userFilters);
    }
  }
  private onCheck(title: string, checkedFltr: IOption, sectionId: number): void {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    // Set checked property more clearly
    if (checkedFltr['checked']) {
      checkedFltr['checked'] = true;
    } else {
      addCheckedProperty(checkedFltr);
    }
    // Space Status handling
    const processedFilter = this.processSpaceStatusFilter(checkedFltr);
    // Handle dropdown filter configuration
    if (this.data?.options?.searchMatchingAvailability && checkedFltr?.inputType === filterTypes.Dropdown) {
      const filter = checkedFltr as any;
      filter.type = filter.type === 'equal' ? 'select' : 'multi-select';
      if (filter.lookup) {
        this.lookupRefService.getDropdownLookUpValues(filter.lookup).subscribe(options => {
          filter.options = [FILTER_EMPTY_VALUE, ...options];
        });
      } else {
        filter.options = [FILTER_EMPTY_VALUE];
      }
    }
    if (userFilter) {
      userFilter.filters = [...userFilter.filters, processedFilter].sort((a, b) => a.key - b.key);
    } else {
      this.userFilters.push({ title, filters: [processedFilter], sectionId });
      this.userFilters.sort((a, b) => a.title.localeCompare(b.title));
    }
  }
  private onUncheck(title: string, uncheckedFltr: IOption): void {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    if (userFilter) {
      userFilter.filters = userFilter.filters.filter(({ key }) => key !== uncheckedFltr.key);
      uncheckedFltr['checked'] = false;
      this.userFilters = cleanUserFilters(this.userFilters);
    }
  }
  private removeMutuallyExclusiveFilter(key: any, sectionId: number): void {
    this.fullCatalog?.filter(section => section.sectionId === sectionId).forEach(section => {
        section?.filters?.forEach(filter => {
        if (filter?.key === key && filter['checked']) {
          this.onUncheck(section?.title, filter);
        }
      });
    });
  }
  private isUserFiltersFull(): boolean {
    if (this.filterForRecords || this.userFilters.length === 0) {
      return true;
    }
    const totalFilters = this.userFilters.flatMap(usrFilter => usrFilter.filters).length;
    return totalFilters < MAX_NUM_FILTERS;
  }
  private searchFilter(termToSearch: string): void {
    if (termToSearch !== '') {
      this.filtersCatalog = this.fullCatalog.map(catElement => ({
        title: catElement.title,
        sectionId: catElement.sectionId,
        filters: catElement.filters.filter(filter => filter.value.toUpperCase().includes(termToSearch.toUpperCase())),
      }));
    } else {
      this.filtersCatalog = this.fullCatalog.map(filters => (filters ? { ...filters } : undefined));
    }
    this.filtersCatalog = cleanUserFilters(this.filtersCatalog);
  }
  onCancel(): void {
    this.dialogRef.close();
  }
  resetToDefaultColumns(): void {
    // Use the active entity's grouping state (works for Space and Availability)
    const isGrouped = !!this.searchDriver?.activeQueryState?.isGroupedView;
    if (this.data?.options['popupFrom'] === POPUP_TYPE.recordList) {
      this.searchDriver?.setDefaultSortAndColumns?.(isGrouped);
      this.dialogRef.close();
    } else {
      // First, reset all checked states in the catalog
      this.filtersCatalog = this.filtersCatalog.map(catalogFilter => ({
        ...catalogFilter,
        filters: catalogFilter.filters.map(filter => ({ ...filter, checked: false })),
      }));
      // Then set user filters to default columns
      this.userFilters = this.createDeepCopy(this.defaultColumns);
      this.filtersCatalog = connectFilters(this.filtersCatalog, this.userFilters);
      const updatedUserFilters = this.createDeepCopy(this.userFilters);
      if (updatedUserFilters.length) {
        updatedUserFilters[0].setDefaultColumns = true;
      }
      this.filtersChange.emit(updatedUserFilters);
    }
  }
  hideLeaseEventsFilter(): void {
    this.filtersCatalog.forEach(section => {
      section.filters = section.filters.filter(filter => !LEASE_EVENT_COLUMNS.includes(filter['columnId']));
    });
  }
  ngOnDestroy(): void {
    // Clean up checked properties
    this.userFilters?.forEach(uf => {
      uf.filters?.forEach(filter => {
        if (filter?.checked) {
          delete filter.checked;
        }
      });
    });
    // Set references to null for garbage collection
    this.userFilters = null;
    this.filtersCatalog = null;
    this.forReset = null;
    this.fullCatalog = null;
  }
  /**
   * Only for Space entity listing page
   */
  private processSpaceStatusFilter(checkedFltr: IOption): IOption {
    if (checkedFltr.key !== SPACE_STATUS_FILTER_KEY) {
      return checkedFltr;
    }
    const currentEntity = this.searchDriver?.activeQueryState?.entity;
    if (!isSpaceEntity(currentEntity)) {
      return checkedFltr;
    }
    const filterCopy = { ...checkedFltr };
    if (!filterCopy.values || (Array.isArray(filterCopy.values) && filterCopy.values.length === 0)) {
      filterCopy.values = [...SPACE_DEFAULT_FILTER_CONFIG[SpaceColumns.SpaceDetails_SpaceStatus].values]; // Create new array to prevent mutation
    }
    return filterCopy;
  }
  /**
   * Find Space Status filter in catalog using constants
   */
  private findSpaceStatusFilter(): { section: ISearchFilterSettings; filter: IOption } | null {
    if (!this.filtersCatalog?.length) return null;
    for (const section of this.filtersCatalog) {
      if (!section.filters?.length) continue;
      const filter = section.filters.find(f => f.key === SPACE_STATUS_FILTER_KEY);
      if (filter) {
        return { section, filter };
      }
    }
    return null;
  }
  /**
   *  Space Status filter has default values
   */
  private ensureSpaceStatusDefaults(): void {
    const currentEntity = this.searchDriver?.activeQueryState?.entity;
    if (!isSpaceEntity(currentEntity)) return;
    for (const userFilter of this.userFilters) {
      for (const filter of userFilter.filters) {
        if (filter.key === SPACE_STATUS_FILTER_KEY) {
          if (!filter.values || (Array.isArray(filter.values) && filter.values.length === 0)) {
            filter.values = [...SPACE_DEFAULT_FILTER_CONFIG[SpaceColumns.SpaceDetails_SpaceStatus].values]; // Create new array to prevent mutation
          }
        }
      }
    }
  }
  /**** Hide Space Tenancy Events Filter && Occupier Type column in Lease && Company ****/
  hideSpaceTenancyEventsFilter(): void {
    this.filtersCatalog.forEach(section => {
      section.filters = section.filters.filter(filter => !SPACE_TENANCY_COMMENTS_COLUMNS.includes(filter['columnId']));
      if (section?.sectionId != null && OCCUPIER_TYPE_SECTION_IDS.includes(section.sectionId) && Array.isArray(section.filters)) {
        section.filters = section.filters.filter(filter => !OCCUPIER_TYPE_COLUMNS.includes(filter['key']));
      }
    });
  }
}
